/**
 * Two-layer visibility resolution for product settings.
 *
 * Layer 1 — User permissions (settings_exclusions):
 *   Application-level isAllowed=true rules enable applications for the user.
 *   For allowed apps: all settings are visible by default, is_active is overridden.
 *   Specific exclusion rules can still hide individual settings within allowed apps.
 *
 * Layer 2 — Product availability (is_active + exclusion specificity):
 *   For non-allowed apps: DB rows and specific exclusion rules compete
 *   in one specificity system. Most specific wins. Ties → show.
 *
 * Hierarchy (shallow → deep):
 *   application(0) > sterile(1) > intendedUse(2) > clinicalUse(3) > custom(4)
 *   > case(5) > practitioner(6) > material(7) > color(8) > polish(9)
 *   > coating(10) > orientation(11)
 *
 * Exclusions are scoped to their level — an exclusion for color=Blue only
 * hides Blue. Other colors and their sub-settings remain unaffected.
 */

const HIERARCHY_LEVEL = {
    application: 0,
    sterile: 1,
    intended_use: 2,
    clinical_use: 3,
    custom: 4,
    case: 5,
    practitioner: 6,
    material: 7,
    color: 8,
    polishing: 9,
    coating: 10,
    orientation: 11,
};

const META_FIELDS = ["id", "isAllowed", "_isSettingRule"];

// Fields that count toward specificity for DB setting rules.
const SETTING_SPECIFICITY_FIELDS = ["application", "material", "color", "polishing", "coating", "intended_use"];

// Maps rule keys to how to extract the corresponding value from a product setting
const RULE_KEY_ACCESSORS = {
    application: (_s, appName) => appName,
    material: (s) => (typeof s.material === "object" && s.material !== null ? s.material.name : s.material),
    color: (s) => (typeof s.color === "object" && s.color !== null ? s.color.name : s.color),
    polishing: (s) => s.polish,
    coating: (s) => s.coating,
    orientation: (s) => s.orientation,
    sterile: (s) => s.sterile,
    intended_use: (s) => s.intendedUse,
    clinical_use: (s) => s.clinicalUse,
    custom: (s) => s.custom,
};

/**
 * Compute specificity: deepestLevel * 100 + fieldCount.
 * For DB setting rules: only count SETTING_SPECIFICITY_FIELDS.
 * For exclusion rules: count all hierarchy fields.
 */
function getSpecificity(rule) {
    const isSettingRule = rule._isSettingRule === true;
    let maxLevel = -1;
    let fieldCount = 0;

    for (const [key, level] of Object.entries(HIERARCHY_LEVEL)) {
        if (rule[key] === null || rule[key] === undefined) continue;
        if (isSettingRule && !SETTING_SPECIFICITY_FIELDS.includes(key)) continue;
        maxLevel = Math.max(maxLevel, level);
        fieldCount++;
    }

    return maxLevel * 100 + fieldCount;
}

/**
 * Check if a rule matches a product setting.
 * Null/undefined rule fields are wildcards (match any value).
 */
function ruleMatchesSetting(rule, setting, applicationName) {
    for (const [ruleKey, getValue] of Object.entries(RULE_KEY_ACCESSORS)) {
        const ruleValue = rule[ruleKey];
        if (ruleValue === null || ruleValue === undefined) continue;
        const settingValue = getValue(setting, applicationName);
        if (settingValue !== ruleValue) return false;
    }
    return true;
}

/**
 * Convert a materials_in_applications row to a rule.
 * Includes ALL fields for matching (prevents cross-contamination between
 * rows that differ only in booleans). Specificity only counts nullable fields.
 */
function settingToRule(setting, applicationName) {
    const extractName = (val) => {
        if (val === null || val === undefined) return null;
        if (typeof val === "object") return val.name ?? null;
        return val;
    };

    return {
        application: applicationName ?? null,
        material: extractName(setting.material),
        color: extractName(setting.color),
        polishing: setting.polish ?? null,
        coating: setting.coating ?? null,
        intended_use: setting.intendedUse ?? null,
        sterile: setting.sterile ?? null,
        clinical_use: setting.clinicalUse ?? null,
        custom: setting.custom ?? null,
        orientation: setting.orientation ?? null,
        isAllowed: setting.is_active ?? true,
        _isSettingRule: true,
    };
}

/**
 * Detect application-level permission rules: isAllowed + only application set.
 */
function isAppLevelPermission(rule) {
    const nonAppKeys = Object.keys(rule).filter((key) => key !== "application" && key !== "id" && key !== "isAllowed");
    return nonAppKeys.every((key) => rule[key] === null || rule[key] === undefined);
}

/**
 * Main entry point.
 */
function excludeSettings(exclusionsInput, allowedCombinations, productSettings) {
    const stripCertFields = ({ certificate_of_conformity, certificate_of_conformity_cost, ...rest }) => rest;

    const allExclusionRules = [
        ...(Array.isArray(allowedCombinations) ? allowedCombinations : []).map(stripCertFields),
        ...exclusionsInput.map(stripCertFields),
    ];

    // --- Layer 1: Application-level user permissions ---
    const allowedApps = allExclusionRules
        .filter((r) => r.isAllowed && isAppLevelPermission(r))
        .map((r) => r.application);

    const excludedApps = allExclusionRules
        .filter((r) => !r.isAllowed && isAppLevelPermission(r))
        .map((r) => r.application)
        .filter((app) => !allowedApps.includes(app));

    // Filter out fully excluded applications
    let result = productSettings.filter((app) => !excludedApps.includes(app.name));

    // --- Layer 2: Product availability ---
    // Specific exclusion rules = everything that is NOT an app-level permission
    const specificExclusionRules = allExclusionRules.filter((r) => !isAppLevelPermission(r));

    if (specificExclusionRules.length === 0) {
        // No specific exclusions — for allowed apps keep everything except
        // settings that are permanently locked off (is_active=false +
        // overridable=false); for others fall back to is_active.
        return result
            .map((application) => {
                if (allowedApps.includes(application.name)) {
                    return {
                        ...application,
                        settings: application.settings.filter(
                            (s) => !(s.is_active === false && s.overridable === false)
                        ),
                    };
                }
                return {
                    ...application,
                    settings: application.settings.filter((s) => s.is_active ?? true),
                };
            })
            .filter((app) => app.settings.length > 0);
    }

    return result
        .map((application) => {
            const isAllowedApp = allowedApps.includes(application.name);

            const filteredSettings = application.settings.filter((setting) => {
                // If overridable=false, is_active is locked — no exclusion can change it.
                if (setting.overridable === false) {
                    return setting.is_active;
                }

                // Only check specific exclusion rules (not all settings — avoids O(n²))
                const matchingRules = specificExclusionRules.filter((rule) =>
                    ruleMatchesSetting(rule, setting, application.name)
                );

                if (matchingRules.length === 0) {
                    // No exclusion matches — allowed apps show, others use is_active
                    return isAllowedApp ? true : setting.is_active ?? true;
                }

                matchingRules.sort((a, b) => getSpecificity(b) - getSpecificity(a));
                const highestSpec = getSpecificity(matchingRules[0]);
                const topRules = matchingRules.filter((r) => getSpecificity(r) === highestSpec);

                if (isAllowedApp) {
                    // Allowed apps: only specific isAllowed=false exclusions can hide.
                    return !topRules.some((r) => !r.isAllowed);
                }

                // Non-allowed apps: exclusion specificity vs is_active specificity.
                // Compute own rule specificity inline (only for settings that actually
                // have matching exclusions — typically a tiny fraction of all settings).
                const ownSpecificity = getSpecificity(settingToRule(setting, application.name));

                if (highestSpec > ownSpecificity) {
                    // Exclusion is more specific → exclusion decides
                    return topRules.some((r) => r.isAllowed);
                } else if (highestSpec === ownSpecificity) {
                    // Tie → show
                    return true;
                }
                // is_active is more specific → use is_active
                return setting.is_active ?? true;
            });

            return { ...application, settings: filteredSettings };
        })
        .filter((app) => app.settings.length > 0);
}

export { excludeSettings };
